import { parseResponse } from '$lib/client/responseHelpers';
import StreamingService from '$lib/services/streaming';
import Emitter from '$lib/services/streaming/eventsDispatcher';
import Events from '$lib/services/streaming/eventsDispatcher/events';
import type { BroadcastConfig } from '$lib/services/streaming/types';

import { ivsRealTimeGetBroadcastConfig, ivsRealTimeRegenerateBroadcastConfig } from '@api/streaming/streaming';
import type { IvsRealtimeBroadcastConfig } from '@api/types';
import { streamingLoggerStore } from '@components/room/streaming-debugger/stores/logger';
import { LogType } from '@shared/types';
import { StreamStatus } from '@shared/types/room';
import { isHttpError } from '@sveltejs/kit';

import type {
    LocalStageStream,
    Stage,
    StageAudioConfiguration,
    StageError,
    StageParticipantPublishState,
    StageStream,
    StageVideoConfiguration,
} from 'amazon-ivs-web-broadcast';

const STAGE_MAX_AUDIO_BITRATE_KBPS = 32;
const STAGE_MAX_BITRATE = 1000;
const STAGE_REDUCED_BITRATE = 200;
const STAGE_MAX_FRAMERATE = 25;
const STAGE_REDUCED_FRAMERATE = 15;

export class IvsBroadcaster {
    private stage?: Stage;
    private statsInterval?: number;
    private regenerateConfig = false;

    public async startBroadcast(config: BroadcastConfig) {
        // Known issue: IVS works only with dynamic import on SSR
        const sdk = (await (import('amazon-ivs-web-broadcast')));
        const {
            StageErrorCode,
            LocalStageStream,
            Stage,
            StageConnectionState,
            StageEvents,
            SubscribeType,
            StageParticipantPublishState,
        } =  sdk;
        Emitter.emit(Events.STREAMING_STATS_SET_BROADCAST_START_TIMER);

        let broadcastConfig: IvsRealtimeBroadcastConfig | null | undefined;
        if (this.regenerateConfig) {
            broadcastConfig = await this.regenerateStageToken();
            this.regenerateConfig = false;
        } else {
            broadcastConfig = await this.getStageTokenWithRetry();
        }

        if (!broadcastConfig) {
            streamingLoggerStore.log({
                type: LogType.AppBroadcast,
                message: 'IvsRealTime: get broadcast config error',
            });
            config.onEvent(StreamStatus.Error);
            return;
        }
        const token = broadcastConfig.broadcastToken;
        const strategy = {
            stageStreamsToPublish() {
                const streams: LocalStageStream[] = [];

                const audioTrack = config.mediaStream.getAudioTracks()[0];
                const audioConfig: StageAudioConfiguration = {
                    stereo: true,
                    maxAudioBitrateKbps: STAGE_MAX_AUDIO_BITRATE_KBPS,
                };
                if (audioTrack) {
                    streams.push(new LocalStageStream(audioTrack, audioConfig));
                }

                const videoTrack = config.mediaStream.getVideoTracks()[0];
                const videoConfig: StageVideoConfiguration = {
                    maxFramerate: config.reduceBitrate
                        ? STAGE_REDUCED_FRAMERATE
                        : STAGE_MAX_FRAMERATE,
                    maxVideoBitrateKbps: config.reduceBitrate
                        ?  STAGE_REDUCED_BITRATE
                        : STAGE_MAX_BITRATE,
                    simulcast: {
                        enabled: !config.reduceBitrate,
                    },
                };
                if (videoTrack) {
                    streams.push(new LocalStageStream(videoTrack, videoConfig));
                }

                return streams;
            },
            shouldPublishParticipant() {
                return true;
            },
            shouldSubscribeToParticipant() {
                return SubscribeType.NONE;
            },
        };
        this.stage = new Stage(token, strategy);
        let publishState: StageParticipantPublishState = StageParticipantPublishState.NOT_PUBLISHED;
        this.stage.on(StageEvents.STAGE_PARTICIPANT_PUBLISH_STATE_CHANGED, (participantInfo, state) => {
            publishState = state;
            if (state === StageParticipantPublishState.ERRORED) {
                streamingLoggerStore.log({
                    type: LogType.AppBroadcast,
                    message: 'IvsRealTime: StageParticipantPublishState ERRORED',
                });
                this.statsCallback('rtc_ivs_pub_error', { errorText: '' });
            }
        });

        this.stage.on(StageEvents.ERROR, (error: StageError) => {
            streamingLoggerStore.log({
                type: LogType.AppBroadcast,
                message: `IvsRealTime Error: ${error.code}, ${error.category}, ${error.message} `
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                + `details: ${(error as any)?.details} `
                + `arn: ${broadcastConfig?.stageArn} `
                + `token: ${token}, `
                + `StringError: ${error.toString()}`,
            });
            switch (error.code) {
                case StageErrorCode.TOKEN_EXPIRED:
                case StageErrorCode.TOKEN_MALFORMED:
                case StageErrorCode.TOKEN_NOT_ALLOWED:
                    this.statsCallback('rtc_ivs_token_error', { errorText: error.message });
                    this.regenerateConfig = true;
                    config.onEvent(StreamStatus.Error);
                    return;
                case StageErrorCode.CODEC_MISMATCH:
                    this.statsCallback('rtc_ivs_codec_error', { errorText: error.message });
                    config.onEvent(StreamStatus.BrowserNotSupported);
                    return;
                case StageErrorCode.FAILED:
                    this.statsCallback('rtc_ivs_failed', { errorText: error.message });
                    config.onEvent(StreamStatus.Error);
                    return;
                case StageErrorCode.CANCELED:
                    this.statsCallback('rtc_ivs_canceled_operation', { errorText: error.message });
                    config.onEvent(StreamStatus.Error);
                    return;
                case StageErrorCode.TIMEOUT:
                    this.statsCallback('rtc_ivs_timeout', { errorText: error.message });
                    config.onEvent(StreamStatus.Error);
                    return;
            }
        });

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        let streamsForStats:StageStream<never>[] | undefined;

        this.stage.on(StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED, (participant, streams) => {
            if (participant.isLocal) {
                streamsForStats = streams;
            }
        });

        this.stage.on(StageEvents.STAGE_CONNECTION_STATE_CHANGED, (state) => {
            switch (state) {
                case StageConnectionState.DISCONNECTED:
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'IvsRealTime: DISCONNECTED',
                    });
                    stopMonitorStats();
                    config.onEvent(StreamStatus.Disconnected);
                    break;
                case StageConnectionState.CONNECTING:
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'IvsRealTime: CONNECTING',
                    });
                    config.onEvent(StreamStatus.SocketOpened);
                    break;
                case StageConnectionState.CONNECTED:
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'IvsRealTime: CONNECTED',
                    });
                    monitorStats();
                    config.onEvent(StreamStatus.PeerConnectionEstablished);
                    break;
                case StageConnectionState.ERRORED:
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'IvsRealTime: ERRORED',
                    });
                    this.statsCallback('rtc_ivs_broadcast_error', { errorText: '' });
                    stopMonitorStats();
                    break;
            }
        });

        const monitorStats = () => {
            stopMonitorStats();
            let lastVideoBytesSent = 0;
            let stalledCount = 0;
            const restartThreshold = 2;
            const checkInterval = 2_000;
            const monitor = async () => {
                if (!this.stage || !streamsForStats) return;
                try {
                    const stats = await Promise.all(
                        streamsForStats.map((stream) => {return stream.requestRTCStats();})
                    );
                    let videoReport: RTCOutboundRtpStreamStats | undefined = undefined;
                    stats.forEach((stat)=>{
                        if (!stat) {
                            return;
                        }
                        stat.forEach((report) => {
                            if (report && report.type === 'outbound-rtp' && report.kind === 'video') {
                                videoReport = report as RTCOutboundRtpStreamStats;
                            }
                        });
                    });

                    if (videoReport) {
                        const videoStats = videoReport as RTCOutboundRtpStreamStats;
                        const currentBytesSent = videoStats.bytesSent;
                        if (!currentBytesSent) return;
                        if (currentBytesSent === lastVideoBytesSent) {
                            stalledCount++;
                            streamingLoggerStore.log({
                                type: LogType.AppBroadcast,
                                message: `IvsRealTime: stalled broadcast: ${stalledCount}`,
                            });
                            this.statsCallback('rtc_ivs_stalled_frame', {});
                        } else {
                            stalledCount = 0;
                        }
                        lastVideoBytesSent = currentBytesSent;
                    } else {
                        if (StreamingService.mediaStreamController.isVideoOn) {
                            stalledCount++;
                            this.statsCallback('rtc_ivs_no_video_in_report', {});
                            streamingLoggerStore.log({
                                type: LogType.AppBroadcast,
                                message: `IvsRealTime: no video in broadcast: ${stalledCount}`,
                            });
                        }
                    }
                    if (
                        !config.mediaStream.active &&
                        publishState === StageParticipantPublishState.ATTEMPTING_PUBLISH
                    ) {
                        this.statsCallback('rtc_ivs_media_stream_need_restart', {});
                    }

                    if (stalledCount >= restartThreshold) {
                        streamingLoggerStore.log({
                            type: LogType.AppBroadcast,
                            message: 'IvsRealTime: stalled broadcast: restart',
                        });
                        this.statsCallback('rtc_ivs_stalled_broadcast_restart', {});
                        stopMonitorStats();
                        config.onEvent(StreamStatus.Error);
                        return;
                    }

                } catch (error) {
                    console.error(error);
                    this.statsCallback('rtc_ivs_error_get_stats', { errorText: (error as Error).message });
                    streamingLoggerStore.log(
                        {
                            type: LogType.AppBroadcast,
                            message: `IvsRealTime: error to get stats`,
                        },
                        { errorText: (error as Error).message },
                        { includeExtendedData: true }
                    );
                }
            };

            this.statsInterval = window.setInterval(monitor, checkInterval);
        };

        const stopMonitorStats = () => {
            window.clearInterval(this.statsInterval);
        };

        try {
            await this.stage.join();
        } catch (error) {
            streamingLoggerStore.log(
                {
                    type: LogType.AppBroadcast,
                    message: `IvsRealTime: Error to join`,
                },
                { errorText: (error as Error).message },
                { includeExtendedData: true }
            );
        }
    }

    public async stopBroadcast() {
        window.clearInterval(this.statsInterval);
        if (!this.stage) {
            return;
        }
        this.stage.leave();
        this.stage.removeAllListeners();
        this.stage = undefined;
    }

    private async getStageToken(): Promise<IvsRealtimeBroadcastConfig | null> {
        try {
            const resp = await ivsRealTimeGetBroadcastConfig();
            const data = parseResponse(resp);
            if (!data) {
                throw new Error('Ivs Realtime Session is not generated');
            }
            return data;
        } catch (error) {
            console.error(error);
            let errorText:string;
            if (isHttpError(error)) {
                errorText = error.body.message;
            } else {
                errorText = (error as Error).message;
            }
            this.statsCallback('rtc_ivs_broadcast_config_error', { errorText });
            return null;
        }
    }
    private async regenerateStageToken(): Promise<IvsRealtimeBroadcastConfig | null> {
        try {
            const configResp = await ivsRealTimeRegenerateBroadcastConfig();
            const data = parseResponse(configResp);

            if (!data) {
                console.error('No config');
                return null;
            }
            return data;
        } catch (error) {
            console.error(error);
            this.statsCallback('rtc_ivs_broadcast_regeneration_config_error', { errorText: (error as Error).message });
            return null;
        }
    }

    private statsCallback(event: string, data: unknown) {
        const streamingType = 'broadcast';
        Emitter.emit(Events.STREAMING_STATS_WEBRTC_SEND_EVENT, event, { ...data || {}, streamingType });
    }

    private async getStageTokenWithRetry() {
        let retryCount = 3;
        while (retryCount) {
            const response = await this.getStageToken();
            if (response) {
                return response;
            }
            console.error('Error to get IvsRealtime broadcast config: retry in 1 sec.');
            retryCount -= 1;
            await new Promise<void>((res) => setTimeout(() => res(), 1000));
        }
        streamingLoggerStore.log({
            type: LogType.AppBroadcast,
            message: 'StreamingProvider: IvsRealtime: getConfigWithRetry: retry exceed',
        });
        console.error('getConfigWithRetry: retry exceed');
        return null;
    }
}